import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return d; }
}

export function InsightsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/insights").then((r) => setItems(r.data || [])); }, []);
  return (
    <div data-testid="insights-page">
      <Seo title="Insights" description="Construction sector market briefs and recruitment insights from B7 Search & Selection." path="/insights" />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-32 pb-12">
        <div className="overline">Insights</div>
        <h1 className="font-serif text-5xl sm:text-7xl mt-6 leading-[0.95] tracking-tight" data-testid="insights-headline">
          Market briefs from the field.
        </h1>
      </section>
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {items.map((p) => (
            <Link key={p.id} to={`/insights/${p.slug}`} className="group" data-testid={`insights-card-${p.slug}`}>
              <div className="aspect-[4/3] overflow-hidden bg-[#E2E0D9]">
                <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="mt-6">
                <div className="overline">{p.category}</div>
                <h3 className="font-serif text-2xl mt-3 leading-tight group-hover:text-[#1E2A78] transition-colors">{p.title}</h3>
                <p className="text-xs text-[#5C5C5C] mt-3">{formatDate(p.published_at)} · {p.source}</p>
                <p className="text-sm text-[#333] mt-4 leading-relaxed line-clamp-3">{p.excerpt}</p>
              </div>
            </Link>
          ))}
          {items.length === 0 && <p className="text-[#5C5C5C]">Loading...</p>}
        </div>
      </section>
    </div>
  );
}

export function InsightDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/insights/${slug}`).then((r) => setItem(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center" data-testid="insight-not-found">
        <h1 className="font-serif text-4xl">Article not found.</h1>
        <Link to="/insights" className="link-underline mt-6 inline-block">Back to insights</Link>
      </div>
    );
  }
  if (!item) return <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-[#5C5C5C]">Loading...</div>;

  return (
    <div data-testid="insight-detail-page">
      <Seo title={item.title} description={item.excerpt} path={`/insights/${item.slug}`} image={item.image_url} />
      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-32 pb-12">
        <Link to="/insights" className="inline-flex items-center gap-2 overline" data-testid="insight-back-link">
          <ArrowLeft size={14} /> Insights
        </Link>
        <div className="overline mt-10">{item.category}</div>
        <h1 className="font-serif text-4xl sm:text-6xl mt-4 leading-[1.02] tracking-tight">{item.title}</h1>
        <p className="text-sm text-[#5C5C5C] mt-6">{formatDate(item.published_at)} · {item.source}</p>
      </section>
      {item.image_url && (
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <img src={item.image_url} alt={item.title} className="w-full aspect-[16/8] object-cover" />
        </div>
      )}
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-16 text-lg leading-relaxed text-[#222]">
        <p className="font-serif text-2xl italic text-[#5C5C5C]">{item.excerpt}</p>
        <div className="mt-8 space-y-6" data-testid="insight-body">
          {(item.body || "").split(/\n{2,}/).filter(Boolean).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </article>
    </div>
  );
}
